import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';

@Directive({
  selector: '[decimalInput]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DecimalInputDirective),
      multi: true,
    },
  ],
})
export class DecimalInputDirective implements ControlValueAccessor {
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>) {}

  @HostListener('input', ['$event.target.value'])
  onInput(value: string): void {
    const normalized = this.normalizeValue(value);
    this.onChange(normalized);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
    const value = this.el.nativeElement.value;
    this.el.nativeElement.value = this.normalizeValue(value);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End',
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x', 'z'].includes(event.key.toLowerCase())) {
      return;
    }

    const isDigit = /^\d$/.test(event.key);
    const isDecimalSeparator = event.key === '.' || event.key === ',';
    const isMinus = event.key === '-';

    if (!isDigit && !isDecimalSeparator && !isMinus) {
      event.preventDefault();
      return;
    }

    const input = this.el.nativeElement;
    const currentValue = input.value;
    const selectionStart = input.selectionStart || 0;

    if (isDecimalSeparator) {
      const normalizedCurrent = currentValue.replace(',', '.');
      if (normalizedCurrent.includes('.')) {
        event.preventDefault();
        return;
      }
    }

    if (isMinus && selectionStart !== 0) {
      event.preventDefault();
      return;
    }
  }

  private normalizeValue(value: string): string {
    if (!value) return '';
    return value.replace(',', '.');
  }

  writeValue(value: any): void {
    const displayValue = value != null ? String(value) : '';
    this.el.nativeElement.value = displayValue;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }
}
